import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Plus, Trash2, GripVertical, LayoutTemplate, ClipboardList, ArrowUp, ArrowDown } from "lucide-react";
import api, { apiError } from "../../api";
import { useI18n } from "../../i18n";
import { Button, Card, Input, Field, Select, Textarea, Modal, Badge, Spinner, EmptyState, cx } from "../../components/ui";
import { toast } from "sonner";

export default function Programs() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("programs");
  const [programs, setPrograms] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [patients, setPatients] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [presetPatient, setPresetPatient] = useState("");

  const load = () => {
    api.get("/programs").then((r) => setPrograms(r.data)).catch(() => setPrograms([]));
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/patients").then((r) => setPatients(r.data)).catch(() => {});
    api.get("/exercises").then((r) => setExercises(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const p = params.get("patient"); const e = params.get("edit");
    if (p) { setPresetPatient(p); setEditing(null); setBuilderOpen(true); }
    if (e && programs) { const prog = programs.find((x) => x.id === e); if (prog) { setEditing(prog); setBuilderOpen(true); } }
  }, [params, programs]);

  const closeBuilder = () => { setBuilderOpen(false); setEditing(null); setPresetPatient(""); setParams({}); };
  const pName = (id) => { const p = patients.find((x) => x.id === id); return p ? `${p.first_name} ${p.last_name}` : id?.slice(0, 8); };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-head font-bold text-navy text-2xl">{t("programs")}</h1>
        <Button className="ms-auto" onClick={() => { setEditing(null); setBuilderOpen(true); }} data-testid="new-program-btn"><Plus size={17} /> {t("build_program")}</Button>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {[["programs", t("programs"), ClipboardList], ["templates", t("templates"), LayoutTemplate]].map(([k, l, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ptab-${k}`}
            className={cx("flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px", tab === k ? "border-teal text-teal" : "border-transparent text-slate-500")}>
            <Icon size={16} /> {l}
          </button>
        ))}
      </div>

      {tab === "programs" && (!programs ? <Spinner /> : programs.length === 0 ? <EmptyState title={t("no_data")} /> : (
        <div className="space-y-2">
          {programs.map((g) => (
            <Card key={g.id} className="p-4 flex flex-wrap items-center gap-3 hover:shadow-card transition cursor-pointer" onClick={() => navigate(`/app/patients/${g.patient_id}`)} data-testid={`program-${g.id}`}>
              <div><div className="font-medium text-navy">{pName(g.patient_id)}</div>
                <div className="text-xs text-slate-400">{g.goal} · {(g.items || []).length} exercices · v{g.version}</div></div>
              <Badge tone={g.status === "actif" ? "green" : "navy"} className="ms-auto">{g.status}</Badge>
              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setEditing(g); setBuilderOpen(true); }}>{t("edit")}</Button>
            </Card>
          ))}
        </div>
      ))}

      {tab === "templates" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((tp) => (
            <Card key={tp.id} className="p-5">
              <div className="flex items-center gap-2 mb-1"><LayoutTemplate size={16} className="text-teal" /><Badge tone="navy">{tp.sharing_level}</Badge></div>
              <h3 className="font-head font-semibold text-navy">{tp.name}</h3>
              <p className="text-sm text-slate-500 mt-1 line-clamp-2">{tp.description}</p>
              <div className="text-xs text-slate-400 mt-2">{(tp.items || []).length} exercices</div>
            </Card>
          ))}
          {templates.length === 0 && <EmptyState title={t("no_data")} />}
        </div>
      )}

      <ProgramBuilder open={builderOpen} onClose={closeBuilder} onDone={load} editing={editing}
        presetPatient={presetPatient} patients={patients} exercises={exercises} templates={templates} t={t} />
    </div>
  );
}

function ProgramBuilder({ open, onClose, onDone, editing, presetPatient, patients, exercises, templates, t }) {
  const [f, setF] = useState({ patient_id: "", goal: "", recommendations: "", precautions: "", reeval_date: "", items: [] });
  const [busy, setBusy] = useState(false);
  const [picker, setPicker] = useState(false);

  useEffect(() => {
    if (editing) setF({ patient_id: editing.patient_id, goal: editing.goal || "", recommendations: editing.recommendations || "",
      precautions: editing.precautions || "", reeval_date: editing.reeval_date || "", items: editing.items || [] });
    else setF({ patient_id: presetPatient || "", goal: "", recommendations: "", precautions: "", reeval_date: "", items: [] });
  }, [editing, presetPatient, open]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const addEx = (ex) => setF((s) => ({ ...s, items: [...s.items, { exercise_id: ex.id, order: s.items.length,
    sets: ex.default_sets, reps: ex.default_reps, duration: ex.default_duration, frequency: ex.default_frequency, rest: ex.default_rest, comment: "" }] }));
  const removeItem = (i) => setF((s) => ({ ...s, items: s.items.filter((_, idx) => idx !== i) }));
  const updItem = (i, k, v) => setF((s) => ({ ...s, items: s.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) }));
  const move = (i, dir) => setF((s) => { const a = [...s.items]; const j = i + dir; if (j < 0 || j >= a.length) return s; [a[i], a[j]] = [a[j], a[i]]; return { ...s, items: a }; });
  const applyTemplate = (id) => { const tp = templates.find((x) => x.id === id); if (tp) set("items", (tp.items || []).map((it, o) => ({ ...it, order: o }))); };
  const exName = (id) => exercises.find((e) => e.id === id)?.name_fr || id?.slice(0, 8);

  const submit = async () => {
    if (!f.patient_id) { toast.error("Sélectionnez un patient"); return; }
    if (f.items.length === 0) { toast.error("Ajoutez au moins un exercice"); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/programs/${editing.id}`, f);
      else await api.post("/programs", f);
      toast.success(editing ? "Programme mis à jour (nouvelle version)" : "Programme prescrit"); onClose(); onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? `${t("edit")} · v${editing.version}` : t("build_program")} size="xl">
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label={t("patients")}>
            <Select value={f.patient_id} onChange={(e) => set("patient_id", e.target.value)} disabled={!!editing} data-testid="pb-patient">
              <option value="">—</option>
              {patients.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
            </Select>
          </Field>
          <Field label={t("reeval_date")}><Input type="date" value={f.reeval_date} onChange={(e) => set("reeval_date", e.target.value)} /></Field>
        </div>
        <Field label={t("program_goal")}><Input value={f.goal} onChange={(e) => set("goal", e.target.value)} data-testid="pb-goal" /></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Recommandations"><Textarea rows={2} value={f.recommendations} onChange={(e) => set("recommendations", e.target.value)} /></Field>
          <Field label={t("precautions")}><Textarea rows={2} value={f.precautions} onChange={(e) => set("precautions", e.target.value)} /></Field>
        </div>

        {!editing && (
          <Field label="Partir d'un modèle (facultatif)">
            <Select onChange={(e) => applyTemplate(e.target.value)} data-testid="pb-template"><option value="">—</option>
              {templates.map((tp) => <option key={tp.id} value={tp.id}>{tp.name}</option>)}</Select>
          </Field>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Exercices ({f.items.length})</span>
            <Button size="sm" variant="outline" onClick={() => setPicker(true)} data-testid="pb-add-exercise"><Plus size={14} /> {t("add_exercise")}</Button>
          </div>
          {f.items.length === 0 ? <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg py-6 text-center">Aucun exercice</div> : (
            <div className="space-y-2">
              {f.items.map((it, i) => (
                <div key={`${it.exercise_id}-${i}`} className="border border-slate-200 rounded-lg p-3 bg-vlg">
                  <div className="flex items-center gap-2 mb-2">
                    <GripVertical size={15} className="text-slate-300" />
                    <span className="font-medium text-navy text-sm flex-1">{i + 1}. {exName(it.exercise_id)}</span>
                    <button onClick={() => move(i, -1)} className="text-slate-400 hover:text-navy p-1"><ArrowUp size={14} /></button>
                    <button onClick={() => move(i, 1)} className="text-slate-400 hover:text-navy p-1"><ArrowDown size={14} /></button>
                    <button onClick={() => removeItem(i)} className="text-alert p-1" data-testid={`pb-remove-${i}`}><Trash2 size={15} /></button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {["sets", "reps", "duration", "frequency", "rest"].map((k) => (
                      <div key={k}><label className="text-[10px] text-slate-400">{t(k)}</label>
                        <Input className="py-1.5 text-xs" value={it[k]} onChange={(e) => updItem(i, k, e.target.value)} /></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>{t("cancel")}</Button>
          <Button onClick={submit} disabled={busy} data-testid="pb-submit">{busy ? t("loading") : t("prescribe")}</Button>
        </div>
      </div>

      <Modal open={picker} onClose={() => setPicker(false)} title={t("add_exercise")} size="lg">
        <div className="grid sm:grid-cols-2 gap-2 max-h-[50vh] overflow-y-auto spina-scroll">
          {exercises.map((e) => (
            <button key={e.id} onClick={() => { addEx(e); setPicker(false); }} data-testid={`pick-ex-${e.id}`}
              className="flex items-center gap-3 p-2 rounded-lg border border-slate-200 hover:border-teal hover:bg-teal-50 text-start">
              {e.image_url && <img src={e.image_url} alt="" className="h-10 w-10 rounded object-cover" />}
              <div className="min-w-0"><div className="text-sm font-medium text-navy truncate">{e.name_fr}</div>
                <div className="text-xs text-slate-400">{(e.category || "").replace(/_/g, " ")}</div></div>
            </button>
          ))}
        </div>
      </Modal>
    </Modal>
  );
}
